import {
  ReactNode,
  createContext,
  useState,
  useContext,
  useEffect,
} from "react";
import Cookies from "js-cookie";
import axiosInstance from "../services/axiosInstance";

interface IUser {
  id: number;
  first_name: string;
  last_name: string;
  is_admin: string;
  is_manager: string;
  email: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  login: (token: string) => void;
  user: IUser | null;
  logout: () => void;
  isLoading: boolean;
}

interface IChildren {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: IChildren) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<IUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = Cookies.get("jwt");
    if (token) {
      setIsAuthenticated(true);
      fetchUser();
    }
    setIsLoading(false); // Finaliza o carregamento
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axiosInstance.get("/me");
      setUser(response.data);
    } catch (error) {
      console.error("Error ao obter informações do usuário", error);
    }
  };

  const login = async (token: string) => {
    Cookies.set("jwt", token, { expires: 1 }); // Armazena o token por 1 dia
    setIsAuthenticated(true);
    await fetchUser();
  };

  const logout = async () => {
    try {
      await axiosInstance.post("/logout");
    } catch (error) {
      console.error("Erro ao realizar o logout", error);
    } finally {
      Cookies.remove("jwt");
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
